import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '@/config/constants';
import { useAuthStore } from '@/store/authStore';
import type { LoginPayload, ForgotPasswordPayload, ResetPasswordPayload, User } from '@/types';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/** Prevent parallel 401s from triggering multiple logouts. */
let handling401 = false;

apiClient.interceptors.request.use((config) => {
  const token =
    localStorage.getItem(AUTH_TOKEN_KEY) ?? useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url ?? '');
    const isAuthEndpoint =
      url.includes('/auth/admin/login') ||
      url.includes('/auth/admin/forgot-password') ||
      url.includes('/auth/admin/reset-password');

    if (status === 401 && !isAuthEndpoint && !handling401) {
      handling401 = true;
      // Clear both localStorage token AND Zustand persist. Clearing only the
      // token key left isAuthenticated=true, so PublicRoute bounced back to
      // dashboard and window.location.href caused an infinite full-page reload.
      useAuthStore.getState().logout();
      setTimeout(() => {
        handling401 = false;
      }, 1000);
    }

    return Promise.reject(error);
  }
);

interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
  message?: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  expiresInSeconds?: number;
}

export const authApi = {
  login: (payload: LoginPayload): Promise<AxiosResponse<LoginResponse>> =>
    apiClient.post('/auth/admin/login', payload),

  forgotPassword: (
    payload: ForgotPasswordPayload,
  ): Promise<AxiosResponse<ForgotPasswordResponse>> =>
    apiClient.post('/auth/admin/forgot-password', payload),

  resetPassword: (
    payload: ResetPasswordPayload,
  ): Promise<AxiosResponse<ForgotPasswordResponse>> =>
    apiClient.post('/auth/admin/reset-password', payload),
};

export default apiClient;
